import React from 'react'
import defaultClasses from './image.module.scss'
import { arrayOf, bool, oneOf, shape, string } from 'prop-types'
import resolveLinkProps from '../../resolveLinkProps'
import { mergeClasses } from '../../classify'
import Link from 'next/link'
import { makeOptimizedUrl as resourceUrl } from '../../makeUrl'
import NextImage from '@corratech/pylot-image'
import { getFastlyUrl } from '@corratech/pylot-image'
import NextHead from 'next/head'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'

const SourceFragment = ({
    mobileImage,
    fastlyOptimizeQuality,
    imageQuality
}) => {
    if (!mobileImage) return null

    return (
        <source
            media="(max-width: 767px)"
            // TODO: fix the logic with resourceURL.
            // Right now it is not used as intended and probably should be removed, as
            // one of the options is to use next image component at the end, which do not require
            // fastly-friendly url
            srcSet={resourceUrl(mobileImage, {
                type: 'image-wysiwyg',
                quality: imageQuality,
                optimizeQuality: fastlyOptimizeQuality
            })}
        />
    )
}

const PicturePB = (props) => {
    const {
        width,
        height,
        resourceSrc,
        classes,
        altText,
        title,
        imageStyles,
        isLazyLoad,
        imageQuality,
        fetchPriority,
        rel
    } = props
    const imageDimensions = { width, height }

    // TODO: think about the flag when to load image
    // with the layout fill

    // if both width and height are numbers
    if (width && height) {
        return (
            <NextImage
                src={resourceSrc}
                className={classes.img}
                alt={altText || title}
                style={imageStyles}
                quality={imageQuality}
                {...imageDimensions}
            />
        )
    }

    const resourceSrcOptimized = getFastlyUrl({
        src: resourceSrc,
        quality: imageQuality
    })

    return (
        <img
            src={resourceSrcOptimized}
            className={classes.img}
            alt={altText || title}
            style={imageStyles}
            {...(fetchPriority && { fetchPriority })}
            {...(width && { width })}
            {...(height && { height })}
            {...(rel === 'preload' && { rel })}
            loading="lazy"
        />
    )
}

/**
 * Page Builder Image component.
 *
 * This component is part of the Page Builder / PWA integration. It can be consumed without Page Builder.
 *
 * @typedef Image
 * @kind functional component
 *
 * @param {props} props React component props
 *
 * @returns {React.Element} A React component that displays an Image.
 */
const Image = (props) => {
    const classes = mergeClasses(defaultClasses, props.classes)
    const {
        desktopImage,
        mobileImage,
        altText,
        title,
        link,
        linkType,
        openInNewTab,
        caption,
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        imageQuality,
        cssClasses = [],
        globalOptions,
        isLazyLoad,
        imgWidth,
        imgHeight,
        imgWidthMob,
        imgHeightMob,
        fetchPriority
    } = props
    const figureStyles = {
        textAlign,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft
    }
    const imageStyles = {
        border,
        borderColor,
        borderWidth,
        borderRadius
    }

    // TODO: think about the isMobile for bageBuilder
    const windowSize = typeof window !== 'undefined' ? window : {}
    const isMobile =
        windowSize.innerWidth <=
        (globalOptions.viewport ? globalOptions.viewport.mobile || 767 : '')

    const mobile = useIsMobile()
    // Don't render anything if there is no image to be rendered
    if (!desktopImage && !mobileImage) {
        return null
    }

    const resourceSrc = resourceUrl(desktopImage, {
        type: 'image-wysiwyg',
        quality: imageQuality,
        optimizeQuality: (globalOptions || {}).fastlyOptimizeQuality
    })

    const PictureWithWrapper = () => {
        const width = isMobile ? imgWidthMob : imgWidth
        const height = isMobile ? imgHeightMob : imgHeight

        const mobileUrl = mobileImage
            ? getFastlyUrl({ src: mobileImage, quality: imageQuality })
            : getFastlyUrl({ src: desktopImage, quality: imageQuality })

        const mobileSlider =
            mobile && cssClasses.includes('plp-banner-img')
                ? 'preload'
                : undefined
        let priority = ''
        if (fetchPriority === 'high' || cssClasses.includes('plp-banner-img')) {
            priority = 'high'
        }
        return (
            <>
                {priority === 'high' && mobile && (
                    <NextHead>
                        <link rel="preload" href={mobileUrl} as="image" />
                    </NextHead>
                )}

                <picture>
                    {/* TODO: start utilizing this component if it brings any benefit. */}
                    <SourceFragment
                        mobileImage={mobileUrl}
                        imageQuality={imageQuality}
                        fastlyOptimizeQuality={
                            (globalOptions || {}).fastlyOptimizeQuality
                        }
                    />
                    <PicturePB
                        width={width}
                        height={height}
                        resourceSrc={resourceSrc}
                        classes={classes}
                        altText={altText}
                        title={title}
                        imageStyles={imageStyles}
                        isLazyLoad={isLazyLoad}
                        imageQuality={imageQuality}
                        fetchPriority={priority}
                        rel={mobileSlider}
                    />
                </picture>
                {caption && <figcaption>{caption}</figcaption>}
            </>
        )
    }

    if (typeof link === 'string') {
        const linkProps = resolveLinkProps(link, linkType)
        return (
            <figure style={figureStyles} className={cssClasses.join(' ')}>
                {linkProps.to ? (
                    <Link {...linkProps}>
                        <a {...(openInNewTab ? { target: '_blank' } : '')}>
                            <PictureWithWrapper />
                        </a>
                    </Link>
                ) : (
                    <a
                        {...linkProps}
                        {...(openInNewTab ? { target: '_blank' } : '')}
                    >
                        <PictureWithWrapper />
                    </a>
                )}
            </figure>
        )
    }

    return (
        <figure style={figureStyles} className={cssClasses.join(' ')}>
            <PictureWithWrapper />
        </figure>
    )
}

/**
 * Props for {@link Image}
 * Props for {@link PicturePB}
 *
 * @typedef props
 *
 * @property {Object} classes An object containing the class names for the Image
 * @property {String} classes.img CSS classes for the img element
 * @property {String} desktopImage URL src of the desktop image
 * @property {String} mobileImage URL src of the mobile image
 * @property {String} altText Alternate text
 * @property {String} title Title of the image
 * @property {String} link URL to redirect to
 * @property {String} linkType Type of link
 * @property {bool} openInNewTab Flag to indicate if link should be opened in a new tab
 * @property {String} caption Caption for the image
 * @property {String} textAlign Alignment of the divider within the parent container
 * @property {String} border CSS border property
 * @property {String} borderColor CSS border color property
 * @property {String} borderWidth CSS border width property
 * @property {String} borderRadius CSS border radius property
 * @property {String} marginTop CSS margin top property
 * @property {String} marginRight CSS margin right property
 * @property {String} marginBottom CSS margin bottom property
 * @property {String} marginLeft CSS margin left property
 * @property {String} paddingTop CSS padding top property
 * @property {String} paddingRight CSS padding right property
 * @property {String} paddingBottom CSS padding bottom property
 * @property {String} paddingLeft CSS padding left property
 * @property {Array} cssClasses List of CSS classes to be applied to the component
 * @property {String} imageQuality Quality of the image
 * @property {String} fetchPriority Priority for image fetching
 * @property {String} rel Priority for image fetching
 * @property {Object} imageStyles Image styes as object
 * @property {String} imgHeightMob Mobile image height
 */
Image.propTypes = {
    classes: shape({
        img: string
    }),
    desktopImage: string,
    mobileImage: string,
    altText: string,
    title: string,
    link: string,
    linkType: oneOf(['default', 'category', 'product', 'page']),
    openInNewTab: bool,
    caption: string,
    textAlign: string,
    border: string,
    borderColor: string,
    borderWidth: string,
    borderRadius: string,
    marginTop: string,
    marginRight: string,
    marginBottom: string,
    marginLeft: string,
    paddingTop: string,
    paddingRight: string,
    paddingBottom: string,
    cssClasses: arrayOf(string),
    imageQuality: string,
    fetchPriority: string,
    rel: string,
    imgHeightMob: string
}

PicturePB.propTypes = {
    width: string,
    height: string,
    resourceSrc: string,
    classes: shape({
        img: string
    }),
    altText: string,
    title: string,
    imageStyles: shape({
        border: string,
        borderColor: string,
        borderWidth: string,
        borderRadius: string
    }),
    isLazyLoad: bool,
    imageQuality: string,
    fetchPriority: string,
    rel: string
}

export default Image
